import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { CreditCard, ArrowUpRight, ReceiptText, CalendarClock, Package } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const STATUS_TONE = {
  paid: "bg-emerald-700 text-white",
  pending: "bg-amber-500 text-white",
  failed: "bg-rose-600 text-white",
  refunded: "bg-stone-500 text-white",
};
const STATUS_LABEL = {
  paid: "Pago",
  pending: "Pendente",
  failed: "Falhou",
  refunded: "Reembolsado",
};
const SUB_STATUS_LABEL = {
  trial: "Período de teste",
  active: "Ativa",
  suspended: "Suspensa",
  expired: "Expirada",
  cancelled: "Cancelada",
};

const money = (v, cur = "brl") =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: (cur || "brl").toUpperCase() }).format(v || 0);

/** FASE 19 — "Meu Plano": current plan, payments made, upcoming charge, change plan. */
export default function Billing() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get("/my-subscription").then(({ data }) => setSub(data)).catch((err) => console.error(err));
    api.get("/billing/payments").then(({ data }) => setPayments(data.payments || [])).catch((err) => console.error(err));
  }, []);

  const plan = sub?.plan;
  const subscription = sub?.subscription;
  const price =
    subscription?.billing_cycle === "yearly" ? plan?.price_yearly : plan?.price_monthly;

  return (
    <Layout
      title="Planos & Cobrança"
      subtitle="Seu plano atual, pagamentos e próximas cobranças"
      actions={
        <Button
          onClick={() => navigate("/pricing")}
          className="bg-emerald-800 hover:bg-emerald-900 text-white"
          data-testid="billing-change-plan"
        >
          <ArrowUpRight className="h-4 w-4 mr-2" /> Mudar de plano
        </Button>
      }
    >
      <div data-testid="billing-page" className="max-w-4xl space-y-6">
        {/* Current plan */}
        <div className="grid md:grid-cols-2 gap-5">
          <Card className="p-6 rounded-xl border-stone-200 bg-white" data-testid="billing-plan-card">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
                <Package className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
                  Plano atual
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  <h3 className="font-display text-2xl font-bold tracking-tight" data-testid="billing-plan-name">
                    {plan?.name || "Sem plano"}
                  </h3>
                  {subscription && (
                    <Badge className={subscription.status === "active" ? "bg-emerald-700 text-white border-none" : "bg-amber-500 text-white border-none"}>
                      {SUB_STATUS_LABEL[subscription.status] || subscription.status}
                    </Badge>
                  )}
                </div>
                {plan ? (
                  <>
                    <div className="font-display text-lg font-bold text-stone-900 mt-2">
                      {money(price)}
                      <span className="text-xs font-body font-normal text-stone-500">
                        /{subscription?.billing_cycle === "yearly" ? "ano" : "mês"}
                      </span>
                    </div>
                    <ul className="mt-2 text-xs text-stone-600 font-body space-y-0.5">
                      <li>Unidades: até {plan.max_units ?? "—"}</li>
                      <li>Usuários: até {plan.max_users ?? "—"}</li>
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-stone-500 font-body mt-2">
                    Nenhuma assinatura ativa. Escolha um plano para começar.
                  </p>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-6 rounded-xl border-stone-200 bg-white" data-testid="billing-next-charge-card">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                <CalendarClock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 font-body">
                  Próxima cobrança
                </div>
                {subscription?.renews_at ? (
                  <>
                    <div className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="billing-next-charge">
                      {formatDateTime(subscription.renews_at)}
                    </div>
                    <p className="text-xs text-stone-500 font-body mt-1">
                      Valor previsto: <strong>{money(price)}</strong> ({subscription.billing_cycle === "yearly" ? "anual" : "mensal"})
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-stone-500 font-body mt-2">
                    {subscription?.status === "trial"
                      ? "Você está no período de teste — nenhuma cobrança agendada."
                      : "Nenhuma cobrança futura agendada."}
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Payments history */}
        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden" data-testid="billing-payments-card">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center gap-2">
            <ReceiptText className="h-4 w-4 text-emerald-700" />
            <h3 className="font-display text-lg font-bold tracking-tight">Pagamentos efetuados</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="font-body">Data</TableHead>
                <TableHead className="font-body">Valor</TableHead>
                <TableHead className="font-body">Método</TableHead>
                <TableHead className="font-body">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-stone-500 py-10 font-body text-sm">
                    <CreditCard className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                    Nenhum pagamento registrado ainda.
                  </TableCell>
                </TableRow>
              )}
              {payments.map((p) => (
                <TableRow key={p.id} data-testid={`billing-payment-${p.id}`}>
                  <TableCell className="font-body text-sm">
                    {formatDateTime(p.paid_at || p.created_at)}
                  </TableCell>
                  <TableCell className="font-display font-semibold">{money(p.amount, p.currency)}</TableCell>
                  <TableCell className="font-body text-sm capitalize">{p.method}</TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_TONE[p.status] || "bg-stone-400 text-white"} border-none`}>
                      {STATUS_LABEL[p.status] || p.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </Layout>
  );
}
